// ============================================================================
// LIT-CASE-002 — Litigation Case Detail Shell (XL Modal) — Phase 9a
// ============================================================================
// Single shell for all 5 litigation case types (ARBITRATION / SARFAESI /
// NI_ACT_138 / EP / CIVIL_SUIT). Per-type variation lives in the section
// registry — Phase 9a wires Summary; later sub-phases plug in Hearings /
// Notices / Orders / per-type detail panels.
//
// Navigation model (Phase 9a-stepper):
//   • <WorkflowChecklistRail> at the top of the sidebar IS the workflow nav.
//     Clicking a step opens the section mapped from that state (via
//     STATE_TO_SECTION_MAP / future tbl_workflow_state.detail_section_code).
//   • Sidebar groups below the rail are CROSS-CUTTING ONLY (Overview,
//     Financial, Records, Audit). Lifecycle and per-type sections are
//     reached only via the stepper — no duplication with the rail.
//   • Selection is mutually exclusive: either a step is active OR a sidebar
//     section is active, never both.
//
// Layout:
//   ┌─ Header: title + status pill + minimise/refresh/close ───────────────┐
//   │  Identity strip: customer · account · product · branch · DPD · OS    │
//   ├──────────────────────┬───────────────────────────────────────────────┤
//   │ Sidebar              │  Active section content                        │
//   │  PROGRESS rail       │   (with stepContext header strip when opened   │
//   │  + cross-cutting     │    via a step)                                 │
//   │    groups            │                                                │
//   └──────────────────────┴───────────────────────────────────────────────┘
//   Footer: Close

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';
import { useLitigationCase, useClaimLitigationCase } from 'hooks/api/useLitigationCases';
import {
  useActiveCaseClosure,
  useCurrentCaseHold,
} from 'hooks/api/useCaseClosure';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import StatusPill, { type PillTone } from 'components/common/StatusPill';
import type { LitigationCase } from 'api/types/litigationCase.types';
import type { OutcomeCategory } from 'api/types/caseClosure.types';
import ResumeCaseModal from './components/ResumeCaseModal';
import {
  WorkflowChecklistRail,
  useWorkflowChecklist,
} from 'recsuit-workflow';
import {
  SIDEBAR_GROUPS,
  visibleSidebarGroups,
  type ResolvedSidebarGroup,
  type SectionMeta,
} from './caseDetailSectionRegistry';
import DynamicStateSection from './sections/DynamicStateSection';
import SummarySection from './sections/SummarySection';

interface LitigationCaseDetailModalProps {
  isOpen: boolean;
  caseId: string | null;
  /** Override the default initial sidebar section ('summary'). Used by directed-open. */
  initialSectionId?: string | null;
  /** Free-form param threaded into the section's render context (e.g. focusGroupId for Group Participations). */
  initialFocusGroupId?: string | null;
  onClose: () => void;
  // Minimise support — same pattern as the Pre-Lit case detail modal.
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (caseId: string, sectionId: string) => void;
}

// ----------------------------------------------------------------------------
// Status pill — single source of truth for the header badge.
// ----------------------------------------------------------------------------

function caseStatusPill(kase: LitigationCase): { label: string; tone: PillTone; pulse: boolean } {
  if (kase.caseStatus === 'CLOSED') {
    return { label: 'Closed', tone: 'neutral', pulse: false };
  }
  if (kase.caseStatus === 'UNCLAIMED') {
    return { label: 'Open · Pending Claim', tone: 'warning', pulse: true };
  }
  const stateName = kase.currentStateDisplayName ?? kase.currentStateCode ?? 'In Progress';
  return { label: `Open · ${stateName}`, tone: 'active', pulse: true };
}

// ----------------------------------------------------------------------------

const LitigationCaseDetailModal: React.FC<LitigationCaseDetailModalProps> = ({
  isOpen, caseId, initialSectionId, initialFocusGroupId, onClose,
  tabId, moduleName, onRestoreModal,
}) => {
  const permissions = useRecsuitPermissions();
  const { minimize } = useModalMinimize();

  const { data: kase, loading, error, refetch } = useLitigationCase(caseId ?? undefined);

  // ---- Workflow checklist (single source of truth for steps + stepContext) ----
  const { data: checklist, loading: checklistLoading, error: checklistError, refetch: refetchChecklist } =
    useWorkflowChecklist(kase?.workflowInstanceId ?? null);

  // ---- Case-level closure + hold state (Phase 7) ----
  const { data: closure, refetch: refetchClosure } = useActiveCaseClosure(isOpen ? caseId : null);
  const { data: hold, refetch: refetchHold } = useCurrentCaseHold(isOpen ? caseId : null);

  const refetchAll = useCallback(async () => {
    await refetch();
    await refetchChecklist();
    await refetchClosure();
    await refetchHold();
  }, [refetch, refetchChecklist, refetchClosure, refetchHold]);

  // ---- Resume modal — fired inline from the hold banner.
  // Close/Reopen/Hold modals are owned by the sidebar lifecycle panels.
  const [resumeOpen, setResumeOpen] = useState(false);

  // ---- Claim flow (UNCLAIMED → IN_PROGRESS + start workflow instance) ----
  const { claim: claimCase, loading: claiming } = useClaimLitigationCase();
  const [claimConfirmOpen, setClaimConfirmOpen] = useState(false);
  const canClaim =
    !!kase &&
    kase.caseStatus === 'UNCLAIMED' &&
    !kase.workflowInstanceId &&
    permissions.hasAction('litigation-case', 'claim');

  const handleConfirmClaim = useCallback(async () => {
    if (!kase) return;
    setClaimConfirmOpen(false);
    try {
      await claimCase(kase.id);
      notificationService.toast.success(`Case ${kase.caseCode} claimed.`);
      await refetch();
      await refetchChecklist();
    } catch (err) {
      notificationService.toast.error(
        err instanceof Error ? err.message : 'Failed to claim case.',
      );
    }
  }, [kase, claimCase, refetch, refetchChecklist]);

  // ---- Mutually exclusive selection ----
  // Either a step is active OR a sidebar section is active. activeSectionId
  // defaults to 'summary' on open (or initialSectionId if the modal was opened
  // via directed-open); clicking a step nulls activeSectionId, and clicking a
  // sidebar section nulls selectedStageCode.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(initialSectionId ?? 'summary');
  const [selectedStageCode, setSelectedStageCode] = useState<string | null>(null);

  // Sidebar group expansion — synced with active sidebar section's parent group.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(SIDEBAR_GROUPS.filter(g => g.defaultExpanded).map(g => g.id)),
  );

  // Refs for closure-stable values used by handleMinimize.
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;
  const activeSectionIdRef = useRef(activeSectionId);
  activeSectionIdRef.current = activeSectionId;

  // Reset on close + honour directed-open (initialSectionId) on open.
  useEffect(() => {
    if (!isOpen) {
      setActiveSectionId('summary');
      setSelectedStageCode(null);
      setExpandedGroups(new Set(SIDEBAR_GROUPS.filter(g => g.defaultExpanded).map(g => g.id)));
    } else if (initialSectionId) {
      // Directed-open: jump to the requested section (and null the stage selection).
      setActiveSectionId(initialSectionId);
      setSelectedStageCode(null);
    }
  }, [isOpen, initialSectionId]);

  // ---- Responsive sidebar (auto-collapse + manual toggle) ----
  // THREE trigger sources combined for robustness — either auto signal
  // flips collapsed true:
  //   A. window.matchMedia('(max-width: 800px)') — catches real-device
  //      narrow viewports (Chrome DevTools device toolbar, real phones).
  //   B. ResizeObserver on the modal element — catches preview-tool device
  //      frames that render the modal in a narrow region without changing
  //      window.innerWidth (Storybook, embedded iframes, etc.).
  //   C. Manual override — user clicks the toggle; their choice wins until
  //      isOpen cycles closed and reopens.
  const [viewportNarrow, setViewportNarrow] = useState(false);
  const [modalNarrow, setModalNarrow] = useState(false);
  const [manualState, setManualState] = useState<boolean | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Signal A: viewport media query.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 800px)');
    const onChange = () => setViewportNarrow(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // legacy
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Signal B: ResizeObserver on the modal element. Re-attaches on every
  // open so a fresh modalRef is always observed.
  useEffect(() => {
    if (!isOpen) return;
    const node = modalRef.current;
    if (!node) return;

    const evaluate = (w: number) => setModalNarrow(w < 800);
    evaluate(node.getBoundingClientRect().width);

    const ro = new ResizeObserver(entries => {
      for (const entry of entries) evaluate(entry.contentRect.width);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [isOpen]);

  // Resolved collapsed state — manual choice wins; otherwise EITHER signal.
  const sidebarCollapsed = manualState != null
    ? manualState
    : (viewportNarrow || modalNarrow);

  // Reset manual override when modal closes so next open re-evaluates auto.
  useEffect(() => {
    if (!isOpen) setManualState(null);
  }, [isOpen]);

  const toggleSidebar = useCallback(() => {
    setManualState(prev => prev != null ? !prev : !(viewportNarrow || modalNarrow));
  }, [viewportNarrow, modalNarrow]);

  // ---- Filtered sidebar groups (cross-cutting only) ----
  const sidebarGroups = useMemo<ResolvedSidebarGroup[]>(
    () => kase ? visibleSidebarGroups(kase, permissions) : [],
    [kase, permissions],
  );

  // ---- Selection handlers (mutually exclusive) ----
  const handleSelectSection = useCallback((sectionId: string) => {
    setActiveSectionId(sectionId);
    setSelectedStageCode(null);
  }, []);

  const handleSelectStage = useCallback((stageCode: string) => {
    setSelectedStageCode(stageCode);
    setActiveSectionId(null);
  }, []);

  // Auto-expand the group containing the active sidebar section.
  useEffect(() => {
    if (!activeSectionId) return;
    const owner = sidebarGroups.find(g => g.sections.some(s => s.code === activeSectionId));
    if (owner) setExpandedGroups(prev => new Set([...prev, owner.id]));
  }, [activeSectionId, sidebarGroups]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const activeSection = useMemo<SectionMeta | null>(() => {
    // Stage-driven view is handled by <DynamicStateSection> directly (renders
    // tabs per inner state from ewf.tbl_workflow_state.form_spec). Sidebar-
    // driven view falls back to the section registry (Summary / Documents /
    // etc.).
    if (selectedStageCode) return null;
    if (activeSectionId) {
      const flat = sidebarGroups.flatMap(g => g.sections);
      return flat.find(s => s.code === activeSectionId) ?? flat[0] ?? null;
    }
    return null;
  }, [selectedStageCode, activeSectionId, sidebarGroups]);

  // ---- "Summary-only" stages — workflow stages whose inner states have NO
  // form fields/sections at all (e.g. DRAFT_BASIC_DETAILS). Rendering the
  // DynamicForm shell for these shows an empty placeholder — confusing for
  // users who expect to see case identity. Detect this and route to
  // <SummarySection> instead so the rail's first step always shows
  // meaningful basic-details content.
  const stageIsSummaryOnly = useMemo(() => {
    if (!selectedStageCode || !checklist) return false;
    const stage = checklist.stages.find((s) => s.stageCode === selectedStageCode);
    if (!stage) return false;
    return stage.steps.every((step) => {
      const spec: any = (step.state as any).formSpec;
      if (!spec) return true;
      const hasFlatFields = Array.isArray(spec.fields) && spec.fields.length > 0;
      const hasSections   = Array.isArray(spec.sections) && spec.sections.length > 0;
      return !hasFlatFields && !hasSections;
    });
  }, [selectedStageCode, checklist]);

  // ---- Minimise handler ----
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentCaseId = caseIdRef.current;
    if (!currentCaseId) return;
    const currentSectionId = activeSectionIdRef.current ?? 'summary';
    const currentCaseCode = kase?.caseCode;
    const currentCaseTypeLabel = kase?.caseTypeDisplayName ?? kase?.caseTypeCode ?? 'Case';

    const modalId = `${tabId}-litigation-case-${currentCaseId}`;
    const title = currentCaseCode
      ? `${currentCaseTypeLabel} · ${currentCaseCode}`
      : `${currentCaseTypeLabel} · ${currentCaseId.slice(0, 8)}`;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: false,
      onRestore: () => {
        if (onRestoreModal) onRestoreModal(currentCaseId, currentSectionId);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, kase?.caseCode, kase?.caseTypeDisplayName, kase?.caseTypeCode]);

  // Footer navigation helpers removed — "View Account 360" and "All Cases"
  // buttons were dropped from the modal footer in Phase 7.17 to keep the
  // shell focused on the case at hand.

  if (!isOpen || !caseId) return null;

  // Loading / error gates inside the modal shell.
  if (loading && !kase) {
    return (
      <>
        <div className="modal-backdrop" />
        <div className="modal-container modal-xl">
          <div className="modal-header" style={{ minHeight: 'auto' }}>
            <h2 className="modal-title">Litigation Case</h2>
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
          <div className="modal-content" style={{ overflow: 'auto' }}>
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading case…
            </div>
          </div>
          <div className="modal-footer" style={{ minHeight: 'auto' }}>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </>
    );
  }

  if (error || !kase) {
    return (
      <>
        <div className="modal-backdrop" />
        <div className="modal-container modal-xl">
          <div className="modal-header" style={{ minHeight: 'auto' }}>
            <h2 className="modal-title">Litigation Case</h2>
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
          <div className="modal-content" style={{ overflow: 'auto' }}>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--form-border-error, #ef4444)' }}>
                {error ? `Failed to load case: ${error.message}` : 'Case not found.'}
              </p>
            </div>
          </div>
          <div className="modal-footer" style={{ minHeight: 'auto' }}>
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </>
    );
  }

  const pill = caseStatusPill(kase);
  const isClosed = kase.caseStatus === 'CLOSED';
  const isHeld = hold?.holdStatus === 'ON_HOLD';
  const canResume = isHeld && permissions.hasAction('litigation-case', 'resume');

  return (
    <>
      <div className="modal-backdrop" />
      <div ref={modalRef} className="modal-container modal-xl" style={{ display: 'flex', flexDirection: 'column' }}>

        {/* ---- Header: title + pill + closure chip (left) + actions + minimise + refresh + close (right) ---- */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
            <h2 className="modal-title" style={{ margin: 0 }}>
              {kase.caseTypeDisplayName ?? kase.caseTypeCode} · {kase.caseCode}
            </h2>
            <StatusPill label={pill.label} tone={pill.tone} pulse={pill.pulse} dot size="sm" />
            {isClosed && closure && <ClosureChip closure={closure} />}
            {isHeld && <HoldChip />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexShrink: 0 }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-header-btn" onClick={refetch} title="Refresh">
              <IconSystem.refresh size={16} />
            </button>
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        {/* ---- Identity strip ---- */}
        <div style={{
          padding: '0.4rem 1.25rem',
          borderBottom: '1px solid var(--border-color)',
          fontSize: '0.82rem', color: 'var(--text-secondary)',
          background: 'var(--bg-secondary)',
        }}>
          <strong style={{ color: 'var(--text-primary)' }}>{kase.customerName ?? '—'}</strong>
          {' · '}{kase.accountNumber ?? '—'}
          {kase.productCode ? ` · ${kase.productCode}` : ''}
          {kase.branchCode ? ` · ${kase.branchCode}` : ''}
          {' · '}DPD {kase.currentDpd ?? 0}
          {' · '}₹{(kase.currentOutstanding ?? 0).toLocaleString('en-IN')}
          <TatChip slaDays={kase.currentStateSlaDays} daysInState={kase.daysInCurrentState} />
        </div>

        {/* ---- Hold banner (yellow) ---- */}
        {isHeld && hold && (
          <div style={{
            padding: '0.5rem 1.25rem',
            background: '#fef3c7', borderBottom: '1px solid #fde68a',
            color: '#78350f', fontSize: '0.82rem',
            display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}>
            <IconSystem.clock size={14} />
            <span>
              <strong>On Hold</strong>
              {hold.holdReasonName ? ` — ${hold.holdReasonName}` : ''}
              {hold.heldAt ? ` since ${new Date(hold.heldAt).toLocaleDateString('en-IN')}` : ''}
              {hold.expectedResumeAt ? `. Expected resume ${new Date(hold.expectedResumeAt).toLocaleDateString('en-IN')}` : ''}
              . Workflow transitions are blocked.
            </span>
            {canResume && (
              <button
                type="button"
                onClick={() => setResumeOpen(true)}
                style={{
                  marginLeft: 'auto',
                  padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600,
                  background: '#d97706', color: 'white',
                  border: 'none', borderRadius: 4, cursor: 'pointer',
                }}
              >Resume</button>
            )}
          </div>
        )}

        {/* ---- Closure banner (subtle, when CLOSED) ---- */}
        {isClosed && closure && (
          <div style={{
            padding: '0.5rem 1.25rem',
            background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)',
            color: 'var(--text-secondary)', fontSize: '0.78rem',
          }}>
            <IconSystem.check size={12} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
            Closed by <strong style={{ color: 'var(--text-primary)' }}>{closure.closedByName}</strong>
            {' on '}{new Date(closure.closedAt).toLocaleDateString('en-IN')}
            {closure.closureName ? ` — ${closure.closureName}` : ''}
            {closure.recoveredAmount != null && closure.recoveredAmount > 0
              ? ` · Recovered ₹${closure.recoveredAmount.toLocaleString('en-IN')}` : ''}
            {closure.closureRemarks && (
              <div style={{ marginTop: '0.25rem', fontStyle: 'italic' }}>
                "{closure.closureRemarks}"
              </div>
            )}
          </div>
        )}

        {/* Workflow transitions are no longer rendered as a global bar here —
             they live inside Case Lifecycle → Workflow Timeline panel,
             alongside the audit history. See WorkflowTimelinePanel. */}

        {/* ---- Two-pane: sidebar + content ---- */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

          {/* Sidebar */}
          <aside style={{
            position: 'relative',
            width: sidebarCollapsed ? 56 : 220,
            flexShrink: 0,
            borderRight: '1px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            overflowY: 'auto',
            padding: '0.1rem 0 0.5rem',
            transition: 'width 0.2s ease',
          }}>
            {/* Toggle — small floating chevron at top-right corner so the
                rail content can start flush with the sidebar top. */}
            <button
              type="button"
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{
                position: 'absolute',
                top: 2,
                right: sidebarCollapsed ? '50%' : 4,
                transform: sidebarCollapsed ? 'translateX(50%)' : 'none',
                border: 'none',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                padding: '0.2rem 0.25rem',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                zIndex: 1,
              }}
            >
              {sidebarCollapsed
                ? <IconSystem.chevronRight size={14} />
                : <IconSystem.chevronLeft size={14} />}
            </button>

            {/* CLAIM CTA — shown above the rail when the case is unclaimed */}
            {!sidebarCollapsed && canClaim && (
              <div style={{
                margin: '0.25rem 0.5rem 0.5rem',
                padding: '0.6rem 0.75rem',
                background: 'var(--bg-primary)',
                border: '1px dashed #2563eb',
                borderRadius: 6,
                fontSize: '0.78rem',
                color: 'var(--text-primary)',
              }}>
                <div style={{ marginBottom: '0.5rem', lineHeight: 1.4 }}>
                  This case is in the unclaimed pool. Claim it to start its workflow.
                </div>
                <button
                  type="button"
                  onClick={() => setClaimConfirmOpen(true)}
                  disabled={claiming}
                  style={{
                    width: '100%',
                    padding: '0.4rem 0.5rem',
                    fontSize: '0.78rem', fontWeight: 600,
                    background: '#2563eb', color: 'white',
                    border: 'none', borderRadius: 4,
                    cursor: claiming ? 'wait' : 'pointer',
                    opacity: claiming ? 0.7 : 1,
                  }}
                >
                  {claiming ? 'Claiming…' : 'Claim Case'}
                </button>
              </div>
            )}

            {/* PROGRESS rail — workflow stepper, always at top */}
            {!sidebarCollapsed && (
              <WorkflowChecklistRail
                data={checklist}
                loading={checklistLoading}
                error={checklistError}
                instanceId={kase.workflowInstanceId}
                selectedStageCode={selectedStageCode}
                onSelectStage={handleSelectStage}
              />
            )}
            {sidebarCollapsed && checklist && (
              <CollapsedRailIndicator
                doneCount={checklist.doneCount}
                totalCount={checklist.totalCount}
                hasSelection={!!selectedStageCode}
                onClick={toggleSidebar}
              />
            )}

            {/* Cross-cutting sidebar groups (no lifecycle / per-type duplication) */}
            {sidebarGroups.map(g => (
              <SectionGroupItem
                key={g.id}
                group={g}
                kase={kase}
                expanded={expandedGroups.has(g.id)}
                onToggle={() => toggleGroup(g.id)}
                activeSectionId={activeSectionId}
                onSelect={handleSelectSection}
                collapsed={sidebarCollapsed}
              />
            ))}
          </aside>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
            {selectedStageCode && stageIsSummaryOnly
              ? <SummarySection kase={kase} />
              : selectedStageCode
                ? <DynamicStateSection kase={kase} stageCode={selectedStageCode} onSaved={refetch} />
                : activeSection
                  ? activeSection.render({
                      kase, permissions, onMutated: refetch,
                      params: { focusGroupId: initialFocusGroupId ?? undefined },
                    })
                  : <div style={{ color: 'var(--text-secondary)' }}>Select a section.</div>}
          </div>
        </div>

        {/* ---- Footer ---- */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Claim confirmation */}
      <ConfirmDialog
        open={claimConfirmOpen}
        title="Claim this case?"
        message={
          `Claiming starts the workflow for ${kase.caseTypeDisplayName ?? kase.caseTypeCode} ` +
          `case ${kase.caseCode} and assigns it to you. This action cannot be undone.`
        }
        confirmLabel="Claim Case"
        cancelLabel="Cancel"
        variant="default"
        loading={claiming}
        loadingMessage="Claiming…"
        onConfirm={handleConfirmClaim}
        onCancel={() => setClaimConfirmOpen(false)}
      />

      {/* Phase 7 — Resume modal only (close/reopen/hold are owned by the
          sidebar lifecycle panels). The hold banner's inline "Resume" button
          opens this one so the user can clear a hold without navigating away. */}
      <ResumeCaseModal
        isOpen={resumeOpen}
        caseId={kase.id}
        caseCode={kase.caseCode}
        onClose={() => setResumeOpen(false)}
        onResumed={refetchAll}
      />
    </>
  );
};

// ----------------------------------------------------------------------------
// Closure + Hold chips for the header row
// ----------------------------------------------------------------------------

const OUTCOME_CHIP: Record<OutcomeCategory, { label: string; bg: string; fg: string }> = {
  FAVOURABLE:   { label: 'Win',     bg: '#dcfce7', fg: '#166534' },
  UNFAVOURABLE: { label: 'Lose',    bg: '#fee2e2', fg: '#991b1b' },
  NEUTRAL:      { label: 'Neutral', bg: 'var(--bg-secondary)', fg: 'var(--text-secondary)' },
};

const ClosureChip: React.FC<{ closure: { outcomeCategory: OutcomeCategory; closureName?: string | null } }> = ({ closure }) => {
  const c = OUTCOME_CHIP[closure.outcomeCategory];
  return (
    <span
      title={closure.closureName ?? closure.outcomeCategory}
      style={{
        fontSize: '0.7rem', fontWeight: 600,
        padding: '2px 10px', borderRadius: '999px',
        background: c.bg, color: c.fg,
      }}
    >
      {c.label}{closure.closureName ? ` · ${closure.closureName}` : ''}
    </span>
  );
};

const HoldChip: React.FC = () => (
  <span style={{
    fontSize: '0.7rem', fontWeight: 600,
    padding: '2px 10px', borderRadius: '999px',
    background: '#fef3c7', color: '#78350f',
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
  }}>
    <IconSystem.clock size={11} /> On Hold
  </span>
);

/** Per-state TAT chip. Hidden when the state has no sla_days configured. */
const TatChip: React.FC<{ slaDays?: number | null; daysInState?: number | null }> = ({ slaDays, daysInState }) => {
  if (slaDays == null || slaDays <= 0) return null;
  const elapsed = daysInState ?? 0;
  const remaining = slaDays - elapsed;
  const isBreach = elapsed > slaDays;
  const isWarn = !isBreach && remaining <= Math.max(1, Math.floor(slaDays * 0.2));
  const tone = isBreach
    ? { bg: '#fee2e2', fg: '#991b1b', label: `TAT breached · ${elapsed - slaDays}d over` }
    : isWarn
      ? { bg: '#fef3c7', fg: '#92400e', label: `TAT · ${remaining}d left of ${slaDays}d` }
      : { bg: '#dcfce7', fg: '#166534', label: `TAT · ${remaining}d left of ${slaDays}d` };
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: 600,
      padding: '2px 10px', borderRadius: '999px',
      background: tone.bg, color: tone.fg,
      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
      marginLeft: '0.4rem',
    }}>
      <IconSystem.clock size={11} /> {tone.label}
    </span>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SectionGroupItem: React.FC<{
  group: ResolvedSidebarGroup;
  kase: LitigationCase;
  expanded: boolean;
  onToggle: () => void;
  activeSectionId?: string | null;
  onSelect: (id: string) => void;
  collapsed: boolean;
}> = ({ group, kase, expanded, onToggle, activeSectionId, onSelect, collapsed }) => {
  // In COLLAPSED mode, suppress group headers entirely; render every section
  // as an icon-only button with title-tooltip. Group separation is preserved
  // via a thin top border on the FIRST section of each group.
  if (collapsed) {
    return (
      <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.25rem', marginTop: '0.25rem' }}>
        {group.sections.map(s => {
          const Icon = (IconSystem as any)[s.icon] ?? IconSystem.info;
          const isActive = activeSectionId === s.code;
          const badgeVal = s.badge ? s.badge(kase) : null;
          return (
            <button
              key={s.code}
              type="button"
              onClick={() => onSelect(s.code)}
              title={`${group.label} · ${s.label}`}
              style={{
                position: 'relative',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '100%', padding: '0.5rem 0',
                color: isActive ? '#2563eb' : 'var(--text-secondary)',
                border: 'none',
                background: isActive ? 'var(--bg-primary)' : 'transparent',
                borderLeft: isActive ? '3px solid #2563eb' : '3px solid transparent',
                cursor: 'pointer',
              }}
            >
              <Icon size={16} />
              {badgeVal != null && badgeVal !== 0 && badgeVal !== '' && (
                <span style={{
                  position: 'absolute', top: 4, right: 6,
                  fontSize: '0.6rem', fontWeight: 600,
                  padding: '0 4px', borderRadius: '999px',
                  background: '#2563eb', color: 'white',
                  minWidth: 14, textAlign: 'center', lineHeight: '14px',
                }}>{badgeVal}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  // EXPANDED mode — group header + section list (original behaviour).
  return (
    <div style={{ marginBottom: '0.25rem' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '0.4rem 0.85rem',
          fontSize: '0.7rem', fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
          border: 'none', background: 'transparent',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span>{group.label}</span>
        {expanded
          ? <IconSystem.chevronDown size={12} />
          : <IconSystem.chevronRight size={12} />}
      </button>
      {expanded && group.sections.map(s => {
        const Icon = (IconSystem as any)[s.icon] ?? IconSystem.info;
        const isActive = activeSectionId === s.code;
        const badgeVal = s.badge ? s.badge(kase) : null;
        return (
          <button
            key={s.code}
            type="button"
            onClick={() => onSelect(s.code)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', padding: '0.4rem 1.5rem',
              fontSize: '0.82rem',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: 'none',
              background: isActive ? 'var(--bg-primary)' : 'transparent',
              borderLeft: isActive ? '3px solid #2563eb' : '3px solid transparent',
              cursor: 'pointer', textAlign: 'left',
              gap: '0.4rem',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
              <Icon size={13} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </span>
            {badgeVal != null && badgeVal !== 0 && badgeVal !== '' && (
              <span style={{
                fontSize: '0.65rem', fontWeight: 600,
                padding: '0 6px', borderRadius: '999px',
                background: isActive ? '#dbeafe' : 'var(--bg-secondary)',
                color: isActive ? '#1e40af' : 'var(--text-secondary)',
              }}>{badgeVal}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// Compact progress indicator shown when sidebar is collapsed.
// Clicking expands the sidebar so the user can interact with the rail.
const CollapsedRailIndicator: React.FC<{
  doneCount: number;
  totalCount: number;
  hasSelection: boolean;
  onClick: () => void;
}> = ({ doneCount, totalCount, hasSelection, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={`Workflow progress: ${doneCount}/${totalCount} (click to expand)`}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem',
      width: '100%', padding: '0.5rem 0',
      color: hasSelection ? '#2563eb' : 'var(--text-secondary)',
      border: 'none', background: 'transparent', cursor: 'pointer',
      borderBottom: '1px solid var(--border-color)',
    }}
  >
    <IconSystem.checkCircle size={16} />
    <span style={{ fontSize: '0.6rem', fontWeight: 600 }}>{doneCount}/{totalCount}</span>
  </button>
);

export default LitigationCaseDetailModal;
